import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Plus, Search } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Header from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import ToggleView from '@/components/ui/toggle-view';
import { toast } from 'sonner';
import UserOrganizations from './dashboard/UserOrganizations';
import UsersTable, { UserData } from './dashboard/components/UsersTable';
import UserFilters from './dashboard/components/UserFilters';
import InviteUserDialog from './dashboard/components/InviteUserDialog';
import OrganizationDialogs from './dashboard/components/OrganizationDialogs';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { fetchUsers, updateUser } from '@/services/users';
import { organizationService, invitationService } from '@/services/api';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { parseLabelValue } from '@/utils/labelUtils';
import { Input } from '@/components/ui/input';
import { 
  Select, 
  SelectTrigger, 
  SelectValue, 
  SelectContent, 
  SelectItem 
} from '@/components/ui/select';

type ActiveView = 'users' | 'organizations';

interface Organization {
  id: string;
  name: string;
  description: string | null;
  membersCount: number;
  createdAt: string;
  labels?: string[];
}

const OrganizationFilters: React.FC<{
  searchTerm: string;
  tagFilter: string;
  organizations: Organization[];
  onSearchChange: (value: string) => void;
  onTagFilterChange: (value: string) => void;
}> = ({ searchTerm, tagFilter, organizations, onSearchChange, onTagFilterChange }) => {
  const allTags = organizations.reduce((tags: string[], org) => {
    if (org.labels) {
      const orgTags = parseLabelValue(org.labels);
      orgTags.forEach(tag => {
        if (!tags.includes(tag)) {
          tags.push(tag);
        }
      });
    }
    return tags;
  }, []);

  return (
    <div className="my-6 flex flex-col gap-4 sm:flex-row">
      <div className="relative flex-1">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search organizations..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-8"
        />
      </div>
      
      <div className="flex gap-2">
        <Select value={tagFilter} onValueChange={onTagFilterChange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            {allTags.map(tag => (
              <SelectItem key={tag} value={tag}>
                {tag}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const UserManagement: React.FC = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [activeView, setActiveView] = useState<ActiveView>('organizations');
  const [users, setUsers] = useState<UserData[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    organization: '',
  });
  const [isCreateOrgDialogOpen, setIsCreateOrgDialogOpen] = useState(false);
  const [isEditOrgDialogOpen, setIsEditOrgDialogOpen] = useState(false);
  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);
  const [newOrg, setNewOrg] = useState({
    name: '',
    description: '',
    labels: [] as string[]
  });
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [isGenerateInviteOpen, setIsGenerateInviteOpen] = useState(false);
  const [inviteKey, setInviteKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [organizationsError, setOrganizationsError] = useState<string | null>(null);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [filteredOrganizations, setFilteredOrganizations] = useState<Organization[]>([]);
  
  useAuthRedirect(false);

  useEffect(() => {
    if (isAuthenticated && !isAdmin) {
      navigate('/dashboard');
      return;
    }
    
    if (isAuthenticated && isAdmin) {
      fetchOrganizations();
      fetchUsersData();
    } else if (!isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated, isAdmin, navigate]);
  
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setOrganizationsError(null);
      
      // Fetch organizations using the organization service
      const orgsData = await organizationService.getOrganizations() as any[];
      
      if (!orgsData || orgsData.length === 0) {
        setOrganizations([]);
        setLoading(false);
        return;
      }
      
      // For each organization, get the count of members
      const orgsWithCounts = await Promise.all(
        orgsData.map(async (org) => {
          try {
            // Get organization details including member count
            const orgDetails = await organizationService.getOrganization(org.id) as any;
            
            return {
              id: org.id,
              name: org.name,
              description: org.description || null,
              membersCount: orgDetails.membersCount || 0,
              createdAt: org.createdAt || new Date().toISOString(),
              labels: org.labels
            };
          } catch (error) {
            console.error('Error getting member count:', error);
            return {
              id: org.id,
              name: org.name,
              description: org.description || null,
              membersCount: 0,
              createdAt: org.createdAt || new Date().toISOString(),
              labels: org.labels
            };
          }
        })
      );
      
      setOrganizations(orgsWithCounts);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizationsError('Failed to load organizations. Please try again later.');
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUsersData = async () => {
    try {
      setLoading(true);
      // Load users using the users service
      const usersData = await fetchUsers();
      
      const transformedUsers = usersData.map(user => ({
        id: user.id,
        name: user.fullName || 'Unnamed User',
        email: user.email || '',
        role: user.isAdmin ? 'admin' as const : 'student' as const,
        status: 'active' as const,
        organization: (user as any).organizations?.[0]?.name || 'No Organization',
        avatarUrl: user.avatarUrl,
        createdAt: user.createdAt,
        progress: user.progress ? {
          availableCourses: user.progress.availableCourses || 0,
          completedCourses: user.progress.completedCourses || 0,
          inProgressCourses: user.progress.inProgressCourses || 0,
          notStartedCourses: user.progress.notStartedCourses || 0
        } : undefined
      }));
      
      setUsers(transformedUsers);
      setFilteredUsers(transformedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    let result = [...users];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        user => 
          user.name.toLowerCase().includes(term) || 
          user.email.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      result = result.filter(user => user.status === statusFilter);
    }
    
    if (roleFilter !== 'all') {
      result = result.filter(user => user.role === roleFilter);
    }
    
    if (organizationFilter !== 'all') {
      result = result.filter(user => user.organization === organizationFilter);
    }
    
    if (sortColumn) {
      result.sort((a, b) => {
        const aValue = a[sortColumn as keyof UserData];
        const bValue = b[sortColumn as keyof UserData];
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    }
    
    setFilteredUsers(result);
  }, [users, searchTerm, statusFilter, roleFilter, organizationFilter, sortColumn, sortDirection]);
  
  useEffect(() => {
    let result = [...organizations];
    
    if (orgSearchTerm) {
      const term = orgSearchTerm.toLowerCase();
      result = result.filter(
        org => 
          org.name.toLowerCase().includes(term) || 
          (org.description?.toLowerCase().includes(term))
      );
    }
    
    if (tagFilter !== 'all') {
      result = result.filter(org => {
        if (!org.labels) return false;
        const tags = parseLabelValue(org.labels);
        return tags.includes(tagFilter);
      });
    }
    
    setFilteredOrganizations(result);
  }, [organizations, orgSearchTerm, tagFilter]);
  
  if (loading) {
    return null;
  }
  
  const handleToggleRole = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    
    try {
      // Use the users service to update user role
      const updatedUserData = await updateUser(userId, { 
        isAdmin: user.role !== 'admin'
      });
      
      if (updatedUserData) {
        const updatedUsers = users.map(u => {
          if (u.id === userId) {
            const newRole = u.role === 'admin' ? 'student' as const : 'admin' as const;
            toast.success(`User role changed to ${newRole}`);
            return { ...u, role: newRole };
          }
          return u;
        });
        
        setUsers(updatedUsers);
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };
  
  const handleToggleStatus = async (userId: string) => {
    // This is just UI state as there's no actual status change in the backend yet
    const updatedUsers = users.map(user => {
      if (user.id === userId) {
        const newStatus = user.status === 'active' ? 'inactive' as const : 'active' as const;
        toast.success(`User status changed to ${newStatus}`);
        return { ...user, status: newStatus };
      }
      return user;
    });
    
    setUsers(updatedUsers);
  };
  
  const handleInviteUser = async () => {
    try {
      if (!selectedOrgId) {
        toast.error('Please select an organization');
        return;
      }
      
      // Generate invitation key using API service
      const response = await invitationService.generateInvitationKey(selectedOrgId);
      setInviteKey((response as any).key || '');
      
      toast.success('Invitation key generated');
    } catch (error) {
      console.error('Error generating invitation key:', error);
      toast.error('Failed to generate invitation key');
    }
  };
  
  const handleCopyInvite = () => {
    navigator.clipboard.writeText(inviteKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Invitation link copied to clipboard');
  };
  
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleCreateOrg = async () => {
    try {
      if (!newOrg.name.trim()) {
        toast.error('Organization name is required');
        return;
      }
      
      // Create org using the API service with labels
      await organizationService.createOrganization(
        newOrg.name.trim(),
        newOrg.description.trim() || undefined,
        newOrg.labels
      );
      
      toast.success('Organization created successfully');
      setIsCreateOrgDialogOpen(false);
      setNewOrg({ name: '', description: '', labels: [] });
      
      // Refresh the organizations list
      fetchOrganizations();
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error('Failed to create organization');
    }
  };

  const handleEditOrg = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setNewOrg({
        name: org.name,
        description: org.description || '',
        labels: parseLabelValue(org.labels)
      });
      setEditingOrgId(orgId);
      setIsEditOrgDialogOpen(true);
    }
  };

  const handleUpdateOrg = async () => {
    if (!editingOrgId) return;
    
    try {
      if (!newOrg.name.trim()) {
        toast.error('Organization name is required');
        return;
      }
      
      // Update org using the API service with labels
      await organizationService.updateOrganization(
        editingOrgId,
        newOrg.name.trim(),
        newOrg.description.trim() || undefined,
        newOrg.labels
      );
      
      toast.success('Organization updated successfully');
      setIsEditOrgDialogOpen(false);
      setNewOrg({ name: '', description: '', labels: [] });
      setEditingOrgId(null);
      
      // Refresh the organizations list
      fetchOrganizations();
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error('Failed to update organization');
    }
  };

  const handleGenerateInvite = async (orgId: string) => {
    setSelectedOrgId(orgId);
    setIsGenerateInviteOpen(true);
    
    try {
      // Generate invitation key using API service
      const response = await invitationService.generateInvitationKey(orgId);
      setInviteKey((response as any).key || '');
    } catch (error) {
      console.error('Error generating invitation:', error);
      toast.error('Failed to generate invitation');
    }
  };

  const handleDeleteOrg = async (orgId: string) => {
    if (!window.confirm('Are you sure you want to delete this organization?')) {
      return;
    }
    
    try {
      // Delete org using the API service
      await organizationService.deleteOrganization(orgId);
      
      toast.success('Organization deleted successfully');
      
      // Refresh the organizations list
      fetchOrganizations();
    } catch (error) {
      console.error('Error deleting organization:', error);
      toast.error('Failed to delete organization');
    }
  };

  const handleSendInvite = async () => {
    try {
      if (!inviteData.email || !inviteData.organization) {
        toast.error('Email and organization are required');
        return;
      }
      
      // Generate invitation key for the specified organization
      const response = await invitationService.generateInvitationKey(inviteData.organization);
      const key = (response as any).key;
      
      // Send email with invitation link
      await fetch('/api/Invitation/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          email: inviteData.email,
          invitationKey: key,
          organizationId: inviteData.organization
        })
      });
      
      toast.success(`Invitation sent to ${inviteData.email}`);
      setIsInviteDialogOpen(false);
      setInviteData({ email: '', organization: '' });
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error('Failed to send invitation');
    }
  };

  const handleNewOrgChange = (data: { name: string; description: string; labels?: string[] }) => {
    setNewOrg({
      name: data.name,
      description: data.description,
      labels: data.labels || []
    });
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <Header 
          title="User & Organization Management" 
          description="Manage users, organizations, and their permissions"
        >
          {activeView === 'users' ? (
            <Button onClick={() => setIsInviteDialogOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Invite User
            </Button>
          ) : (
            <Button onClick={() => setIsCreateOrgDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Organization
            </Button>
          )}
        </Header>
        
        <ToggleView 
          activeView={activeView} 
          onChange={(view) => setActiveView(view as ActiveView)} 
        />
        
        {activeView === 'organizations' ? (
          // Show loading state when loading
          loading ? (
            <div className="mt-6 mb-8">
              <h2 className="text-xl font-semibold mb-4">Organizations</h2>
              <Card>
                <CardContent className="p-6 space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </CardContent>
              </Card>
            </div>
          ) : (
            <>
              <OrganizationFilters
                searchTerm={orgSearchTerm}
                tagFilter={tagFilter}
                organizations={organizations}
                onSearchChange={setOrgSearchTerm}
                onTagFilterChange={setTagFilter}
              />
              <UserOrganizations 
                organizations={filteredOrganizations.length > 0 || orgSearchTerm || tagFilter !== 'all' 
                  ? filteredOrganizations 
                  : organizations}
                isLoading={loading}
                error={organizationsError}
                showAsAdmin={true}
                onEditOrganization={handleEditOrg}
                onGenerateInvite={handleGenerateInvite}
                onDeleteOrganization={handleDeleteOrg}
              />
            </>
          )
        ) : (
          <>
            <UserFilters
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              roleFilter={roleFilter}
              organizationFilter={organizationFilter}
              organizations={organizations}
              onSearchChange={setSearchTerm}
              onStatusFilterChange={setStatusFilter}
              onRoleFilterChange={setRoleFilter}
              onOrganizationFilterChange={setOrganizationFilter}
            />
            
            <UsersTable
              users={filteredUsers}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onToggleRole={handleToggleRole}
              onToggleStatus={handleToggleStatus}
              onSort={handleSort}
            />
          </>
        )}
        
        <InviteUserDialog
          isOpen={isInviteDialogOpen}
          organizations={organizations}
          inviteData={inviteData}
          onClose={() => setIsInviteDialogOpen(false)}
          onInviteDataChange={setInviteData}
          onSendInvite={handleSendInvite}
        />
        
        <OrganizationDialogs
          isCreateOpen={isCreateOrgDialogOpen}
          isEditOpen={isEditOrgDialogOpen}
          isGenerateInviteOpen={isGenerateInviteOpen}
          newOrg={newOrg}
          inviteKey={inviteKey}
          copied={copied}
          onCreateOrgClose={() => setIsCreateOrgDialogOpen(false)}
          onEditOrgClose={() => setIsEditOrgDialogOpen(false)}
          onGenerateInviteClose={() => setIsGenerateInviteOpen(false)}
          onNewOrgChange={handleNewOrgChange}
          onCreateOrg={handleCreateOrg}
          onUpdateOrg={handleUpdateOrg}
          onCopyInvite={handleCopyInvite}
        />
      </main>
    </div>
  );
};

export default UserManagement;
